import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";
import {ToastService} from "src/app/modules/core/services/toast.service";
import {
  BeforeInstallPromptEvent,
  NavigatorWithStandalone,
} from "../interfaces/pwaInstall.interface";
import {PlatformService} from "src/app/services/platform.service";

@Injectable({providedIn: "root"})
export class PwaInstallService {
  private readonly manualInstallHintDurationMs = 10000;
  private readonly canInstallSubject = new BehaviorSubject<boolean>(false);
  private readonly isInstalledSubject = new BehaviorSubject<boolean>(false);
  private readonly manualInstallHintSubject = new BehaviorSubject<
    string | null
  >(null);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private nativePromptWasHandled = false;

  constructor(
    private readonly toastService: ToastService,
    private readonly platformService: PlatformService
  ) {
    if (this.platformService.isNativePlatform()) {
      this.isInstalledSubject.next(true);
      return;
    }

    this.detectInstalledState();
    this.listenToBeforeInstallPrompt();
    this.listenToAppInstalled();
    this.listenToDisplayModeChanges();
    this.enableManualInstallFallback();
  }

  public getCanInstall$(): Observable<boolean> {
    return this.canInstallSubject.asObservable();
  }

  public getIsInstalled$(): Observable<boolean> {
    return this.isInstalledSubject.asObservable();
  }

  public async triggerInstallPrompt(): Promise<void> {
    if (!this.deferredPrompt) {
      this.showManualInstallHint();
      return;
    }

    const promptEvent = this.deferredPrompt;
    await promptEvent.prompt();

    const choice = await promptEvent.userChoice;

    this.deferredPrompt = null;
    this.nativePromptWasHandled = true;
    this.updateCanInstallState();

    if (choice.outcome === "accepted") {
      console.log("User accepted the install prompt");
    } else {
      console.log("User dismissed the install prompt");
    }
  }

  private detectInstalledState(): void {
    const navigatorWithStandalone =
      globalThis.navigator as NavigatorWithStandalone;

    const isStandalone: boolean = globalThis.matchMedia(
      "(display-mode: standalone)"
    ).matches;

    const isIosStandalone: boolean =
      navigatorWithStandalone.standalone === true;

    const isFromAndroidApp: boolean =
      globalThis.document.referrer.startsWith("android-app://");

    const installed: boolean =
      isStandalone || isIosStandalone || isFromAndroidApp;

    this.isInstalledSubject.next(installed);
    this.refreshManualInstallHint();
    this.updateCanInstallState();
  }

  private listenToBeforeInstallPrompt(): void {
    globalThis.addEventListener("beforeinstallprompt", (event: Event) => {
      event.preventDefault();

      if (this.isInstalledSubject.value) return;

      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.nativePromptWasHandled = false;
      this.updateCanInstallState();
    });
  }

  private listenToAppInstalled(): void {
    globalThis.addEventListener("appinstalled", () => {
      this.isInstalledSubject.next(true);
      this.deferredPrompt = null;
      this.nativePromptWasHandled = true;
      this.refreshManualInstallHint();
      this.updateCanInstallState();
    });
  }

  private listenToDisplayModeChanges(): void {
    const mediaQueryList: MediaQueryList = globalThis.matchMedia(
      "(display-mode: standalone)"
    );

    mediaQueryList.addEventListener("change", () => {
      this.detectInstalledState();
    });
  }

  private enableManualInstallFallback(): void {
    globalThis.setTimeout(() => {
      this.refreshManualInstallHint();
      this.updateCanInstallState();
    }, 1500);
  }

  private refreshManualInstallHint(): void {
    const installHint = this.getManualInstallHint();

    this.manualInstallHintSubject.next(installHint);
  }

  private updateCanInstallState(): void {
    const canInstallFromPrompt: boolean =
      this.deferredPrompt !== null && !this.isInstalledSubject.value;
    const canInstallManually: boolean =
      this.manualInstallHintSubject.value !== null &&
      !this.isInstalledSubject.value &&
      !this.nativePromptWasHandled;

    this.canInstallSubject.next(canInstallFromPrompt || canInstallManually);
  }

  private showManualInstallHint(): void {
    const manualInstallHint =
      this.manualInstallHintSubject.value ??
      this.getManualInstallHint() ??
      "GENERIC_INSTALL_APP_HINT";

    console.info("[PWA] Native install prompt is not available", {
      hint: manualInstallHint,
      userAgent: globalThis.navigator.userAgent,
    });

    this.toastService.sendToast({
      text: manualInstallHint,
      translatable: true,
      classes: "info",
      durationMs: this.manualInstallHintDurationMs,
    });
  }

  private getManualInstallHint(): string | null {
    if (this.isInstalledSubject.value || this.isInStandaloneMode()) {
      return null;
    }

    if (!this.isSecureOrigin()) {
      return "GENERIC_INSTALL_APP_HINT";
    }

    if (this.isIos()) {
      return "IOS_INSTALL_APP_HINT";
    }

    if (this.isAndroid()) {
      return "ANDROID_INSTALL_APP_HINT";
    }

    if (this.isChromiumBasedBrowser()) {
      return "CHROMIUM_INSTALL_APP_HINT";
    }

    if (this.isSafariOnMac()) {
      return "SAFARI_DESKTOP_INSTALL_APP_HINT";
    }

    if (this.isFirefox()) {
      return this.isFirefoxWindowsWithWebApps()
        ? "FIREFOX_WINDOWS_INSTALL_APP_HINT"
        : "FIREFOX_DESKTOP_INSTALL_UNAVAILABLE_HINT";
    }

    return "GENERIC_INSTALL_APP_HINT";
  }

  private isInStandaloneMode(): boolean {
    const navigatorWithStandalone =
      globalThis.navigator as NavigatorWithStandalone;

    return (
      globalThis.matchMedia("(display-mode: standalone)").matches ||
      navigatorWithStandalone.standalone === true
    );
  }

  private isSecureOrigin(): boolean {
    const hostname: string = globalThis.location.hostname;

    return (
      globalThis.isSecureContext ||
      hostname === "localhost" ||
      hostname === "127.0.0.1"
    );
  }

  private isAndroid(): boolean {
    return /Android/i.test(globalThis.navigator.userAgent);
  }

  private isIos(): boolean {
    const userAgent: string = globalThis.navigator.userAgent;
    const platform: string = globalThis.navigator.platform;
    const maxTouchPoints: number = globalThis.navigator.maxTouchPoints ?? 0;
    const iPadOs13Plus: boolean = platform === "MacIntel" && maxTouchPoints > 1;

    return /iPad|iPhone|iPod/.test(userAgent) || iPadOs13Plus;
  }

  private isChromiumBasedBrowser(): boolean {
    const userAgent: string = globalThis.navigator.userAgent;
    const isChromiumLike: boolean =
      /Chrome|Chromium|CriOS|Edg|OPR|SamsungBrowser/i.test(userAgent);
    const isSafari: boolean = /Safari/i.test(userAgent) && !isChromiumLike;

    return isChromiumLike && !this.isFirefox() && !isSafari;
  }

  private isSafariOnMac(): boolean {
    const userAgent: string = globalThis.navigator.userAgent;

    return (
      /Macintosh/i.test(userAgent) &&
      /Safari/i.test(userAgent) &&
      !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/i.test(userAgent)
    );
  }

  private isFirefox(): boolean {
    return /Firefox|FxiOS/i.test(globalThis.navigator.userAgent);
  }

  private isFirefoxWindowsWithWebApps(): boolean {
    const userAgent: string = globalThis.navigator.userAgent;
    const firefoxVersion = /Firefox\/(\d+)/i.exec(userAgent);

    if (!firefoxVersion) return false;

    const majorVersion = Number(firefoxVersion[1]);

    return /Windows/i.test(userAgent) && majorVersion >= 143;
  }
}
